import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { umaPrograms, UMA_TCPA_DISCLOSURE } from '../data/umaPrograms';
import { CLINICAL_PROGRAM_SLUG, isClinicalZip } from '../data/umaClinicalZips';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { LegalLinks } from '../components/LegalLinks';
const umaEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}, {
  label: 'Graduate Degree',
  value: '8'
}];
const umaStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
type Panel = 'zip' | 'program' | 'background' | 'location' | 'contact';
/**
 * Resolves a program from the URL so a campaign link can skip the picker step.
 * Accepts ?program= (slug or route slug) or ?mProgram= (webhook code).
 */
function getProgramSlugFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const requested = (params.get('program') || params.get('mProgram') || '').trim();
  if (!requested) return '';
  const match = umaPrograms.find(p => p.slug.toLowerCase() === requested.toLowerCase() || p.routeSlug.toLowerCase() === requested.toLowerCase() || p.mProgram.toLowerCase() === requested.toLowerCase());
  return match?.slug || '';
}
interface UMALandingPageProps {
  /**
   * Asks for the ZIP before the program picker. Used by
   * /find-programs/ultimate-medical-academy, where the visitor arrives without
   * a program in mind and location is the lowest-commitment opening question.
   */
  zipFirst?: boolean;
  /** Where the thank-you page sends the visitor back to. */
  backUrl?: string;
  /** Shows UMA's own logo bar above the hero. */
  showBrandHeader?: boolean;
}
export function UMALandingPage({
  zipFirst = false,
  backUrl = '/colleges/ultimate-medical-academy',
  showBrandHeader = true
}: UMALandingPageProps = {}) {
  usePageMeta('Ultimate Medical Academy Online | Healthcare Career Training Programs', 'Explore online healthcare degree programs at Ultimate Medical Academy. Get personalized support, financial aid options, and job search assistance. Request info today.', {
    ogImage: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [preselectedProgram] = useState(getProgramSlugFromUrl);
  const [selectedProgram, setSelectedProgram] = useState(preselectedProgram);
  // The funnel is ordered rather than hard-coded so the /find-programs variant
  // can open on location without a second copy of the form.
  const panels = React.useMemo<Panel[]>(() => {
    const base: Panel[] = zipFirst ? ['zip', 'program', 'background', 'location', 'contact'] : ['program', 'background', 'location', 'contact'];
    return preselectedProgram ? base.filter(p => p !== 'program') : base;
  }, [zipFirst, preselectedProgram]);
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  // Fetch user IP on mount
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const currentPanel = panels[formStep];
  const [programError, setProgramError] = useState('');
  /**
   * Clinical Medical Assistant requires an in-person externship, so it is only
   * offered inside its placement area. Once a ZIP is known, an out-of-area
   * visitor never sees a program they cannot enroll in.
   */
  const availablePrograms = React.useMemo(() => {
    const zip = formData.zipCode.replace(/\D/g, '');
    if (zip.length < 5 || isClinicalZip(zip)) return umaPrograms;
    return umaPrograms.filter(p => p.slug !== CLINICAL_PROGRAM_SLUG);
  }, [formData.zipCode]);
  // A ZIP entered after the fact can invalidate an earlier selection.
  React.useEffect(() => {
    if (selectedProgram && !availablePrograms.some(p => p.slug === selectedProgram)) {
      setSelectedProgram('');
    }
  }, [availablePrograms, selectedProgram]);
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSlug = e.target.value;
    setSelectedProgram(newSlug);
    if (newSlug) setProgramError('');
    // Picking a program answers the whole panel, so advance rather than
    // making the visitor confirm a choice they just made.
    if (newSlug && currentPanel === 'program') {
      setFormStep(formStep + 1);
    }
  };
  const goBack = () => setFormStep(Math.max(0, formStep - 1));
  const handleNext = () => {
    if (currentPanel === 'zip') {
      if (!validateUniStep2()) return;
      // Resolved here so the later location step opens already filled in.
      lookupZip(formData.zipCode);
    } else if (currentPanel === 'program') {
      if (!selectedProgram) {
        setProgramError('Please choose a program to continue.');
        return;
      }
    } else if (currentPanel === 'background') {
      if (!validateUniStep1()) return;
    } else if (currentPanel === 'location') {
      if (!validateUniStep2()) return;
      // The address fields only appear once the ZIP resolves, so reveal them
      // rather than erroring on a field the visitor cannot see yet.
      if (!zipLookedUp) {
        lookupZip(formData.zipCode);
        return;
      }
      const addrValid = formData.address.trim().length > 0;
      setAddressError(addrValid ? '' : 'Street address is required.');
      if (!addrValid) return;
    }
    setFormStep(formStep + 1);
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      const currentProgram = umaPrograms.find(p => p.slug === selectedProgram);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/u7uetpg/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'Ultimate Medical Academy',
            logo: 'https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp',
            primaryColor: '#003B5C',
            accentColor: '#00BFA5',
            backUrl,
            programTitle: currentProgram?.title || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = panels.length;
  const progressPercent = Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`;
  const currentProgramTitle = umaPrograms.find(p => p.slug === selectedProgram)?.title;
  const renderFormContent = () => {
    if (currentPanel === 'zip') {
      return <div className="space-y-4" data-id="element-3237" data-mp="1apgups0o5nqe">
          <div data-id="element-3238" data-mp="15qe2x99p9e0d">
            <label htmlFor="startZip" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3239" data-mp="15qx8rrm2sby1">
              Zip Code
            </label>
            <input type="text" id="startZip" inputMode="numeric" autoComplete="postal-code" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-3240" data-mp="1jjdhn91nr46v" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-3241" data-mp="hnhgqq29s8ey">{errors.zipCode}</p>}
          </div>
          <p className="text-xs text-gray-500" data-id="element-3242" data-mp="2yb7e99px1tc">
            We use your zip code to show the programs available in your area.
          </p>
          <button type="button" onClick={handleNext} className="w-full bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-3243" data-mp="7ww9yfcfzxky">
            Next
          </button>
        </div>;
    }
    if (currentPanel === 'program') {
      return <div className="space-y-4" data-id="element-3244" data-mp="qmufoosrpelf">
          <div data-id="element-3245" data-mp="qlt4mkbr4s3p">
            <label htmlFor="hero-program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3246" data-mp="qljfeyyj6oz2">
              Select a Program
            </label>
            <div className="relative" data-id="element-3247" data-mp="3be4p2rfscjbz">
              <select id="hero-program-select" value={selectedProgram} onChange={handleProgramChange} className={`block w-full px-4 py-3 text-sm border rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-800 ${programError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`} data-id="element-3248" data-mp="3bayzlb38vffq">
                <option value="" data-id="element-3249" data-mp="13689gj5ays5z">-- Choose a Program --</option>
                {availablePrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-3250" data-mp="y751oqxxu7ub">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-3251" data-mp="u48r9nijxi1g">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3252" data-mp="p678z4sy9mr1" />
              </div>
            </div>
            {programError && <p className="mt-1 text-xs text-red-600" data-id="element-3253" data-mp="k8yogxkbx6mm">{programError}</p>}
          </div>
          {formStep === 0 ? <p className="text-xs text-gray-500 text-center" data-id="element-3254" data-mp="2jhvn54rcxlad">
              Select a program above to get started
            </p> : <div className="flex gap-3 pt-2" data-id="element-3255" data-mp="2m4ec5yh10287">
              <button type="button" onClick={goBack} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3256" data-mp="2m3moraxl1at0">
                <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3257" data-mp="2vwaj7z13b2ok" />
                Back
              </button>
              <button type="button" onClick={handleNext} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-3258" data-mp="30vdbsjzvvgfn">
                Next
              </button>
            </div>}
        </div>;
    }
    if (currentPanel === 'background') {
      return <div className="space-y-4" data-id="element-3259" data-mp="2i3knv3zo8les">
          <div data-id="element-3260" data-mp="2i0gb3i854hci">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3261" data-mp="3oj50kn2m1lxt">
              Program
            </label>
            <div className="relative" data-id="element-3262" data-mp="3tk2icyjpkdys">
              <select id="step1-program" value={selectedProgram} onChange={handleProgramChange} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-3263" data-mp="3r2rlfapu8hyr">
                {availablePrograms.map(p => <option key={p.slug} value={p.slug} data-id="element-3264" data-mp="4tqev8n2qj24">
                    {p.title}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-3265" data-mp="dw9hbrmy6tnu">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3266" data-mp="9u4q0zjz294d" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-3267" data-mp="nl9h3d99xt33" />

          <div data-id="element-3268" data-mp="inriafoz0u8t">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3269" data-mp="in7yetfrfvs5">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-3270" data-mp="slxd6dcd1046">
              <option value="" data-id="element-3271" data-mp="1c5mkovn0uq4b">-- Please Select --</option>
              {umaStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-3272" data-mp="1g6ydfa16zrgq">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-3273" data-mp="177l20bticstd">{errors.startDate}</p>}
          </div>

          <div data-id="element-3274" data-mp="sj83p86y6xvp">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3275" data-mp="sif2emqrd0ky">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-3276" data-mp="jjl2oni4ru36">
              <option value="" data-id="element-3277" data-mp="9kcb21aqmrjz">-- Please Select --</option>
              {umaEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-3278" data-mp="ek8hs8atkx38">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-3279" data-mp="3rmas2rphhyi8">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-3280" data-mp="3htvd839hhq1k">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3281" data-mp="3hvpwld53xa3j">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-3282" data-mp="ee7sngsggn8e">
              <option value="" data-id="element-3283" data-mp="9d0mfm5ynw3u">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-3284" data-mp="4huyyiwwu9dn">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-3285" data-mp="2p5r2h3c22ms0">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-3286" data-mp="2k76479gd6d67">
            {formStep > 0 && <button type="button" onClick={goBack} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3287" data-mp="2k5l83feeim68">
                <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3288" data-mp="14lm3uby25wtm" />
                Back
              </button>}
            <button type="button" onClick={handleNext} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-3289" data-mp="1edrqr79gdo1y">
              Next
            </button>
          </div>
        </div>;
    }
    if (currentPanel === 'location') {
      return <div className="space-y-4" data-id="element-3290" data-mp="1se2hmaarj2n4">
          <div data-id="element-3291" data-mp="1xcm5whctwqin">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3292" data-mp="1xd5ps3m1hozb">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-3293" data-mp="3u70i8sas6zim" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-3294" data-mp="2eo16vj7od4md">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-3295" data-mp="29nn9cv3p9d2l">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-3296" data-mp="1lyc7196pbamc" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-3297" data-mp="1vw02k1c4sm5k">
                <div data-id="element-3298" data-mp="1vtdttydi80lr">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3299" data-mp="1qwne5c5m370v">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#4ECDC4] focus:border-[#4ECDC4] bg-gray-50" placeholder="City" data-id="element-3300" data-mp="37cqbvtbupu3e" />
                </div>
                <div data-id="element-3301" data-mp="3ers7v15pc7x2">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3302" data-mp="30un5pc93zt73">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#4ECDC4] focus:border-[#4ECDC4] bg-gray-50" placeholder="State" data-id="element-3303" data-mp="34xs71bhgdou5" />
                </div>
              </div>
              <div data-id="element-3304" data-mp="3nub77baa42ek">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3305" data-mp="3ntrqfxwd7k24">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#4ECDC4] focus:border-[#4ECDC4]'}`} placeholder="123 Main St" data-id="element-3306" data-mp="k69ljj64zitg" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-3307" data-mp="26ysxbl77yd5u">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-3308" data-mp="1syrqlp49wbig">
            {formStep > 0 && <button type="button" onClick={goBack} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3309" data-mp="1xzpgyozfsbv1">
                <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3310" data-mp="1o219cygqf9vg" />
                Back
              </button>}
            <button type="button" onClick={handleNext} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-3311" data-mp="3dhhdu0jpo7f0">
              Next
            </button>
          </div>
        </div>;
    }
    if (currentPanel === 'contact') {
      return <div className="space-y-4" data-id="element-3312" data-mp="1gi8rsg8do15k">
          <div className="grid grid-cols-2 gap-3" data-id="element-3313" data-mp="1giih01llm4a7">
            <div data-id="element-3314" data-mp="1braapefkez19">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3315" data-mp="1bqqq1pyj5wjj">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-3316" data-mp="1lgts0fnmqku0" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-3317" data-mp="1uh05w2n8pmtp">{errors.firstName}</p>}
            </div>
            <div data-id="element-3318" data-mp="521yfzeqz7j4">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3319" data-mp="53muxukmh0jm">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-3320" data-mp="205zzdej7p3kw" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-3321" data-mp="29658pu7yuc1p">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-3322" data-mp="2e6kjqf4rz7gi">
            <div data-id="element-3323" data-mp="2e3549b76vwek">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3324" data-mp="2j4ueioml861l">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-3325" data-mp="2o1kt17iqutkw" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-3326" data-mp="31v3sswvnfo9n">{errors.email}</p>}
            </div>
            <div data-id="element-3327" data-mp="31t94r9p2zg1c">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3328" data-mp="36tx72rirfjav">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-3329" data-mp="3gw0taah4d1ls" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-3330" data-mp="32yudmorozj0s">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-5 w-5 rounded border-gray-300 text-[#00BFA5] focus:ring-[#00BFA5] cursor-pointer flex-shrink-0" data-id="element-3331" data-mp="3jgyndujn4ly">
            {UMA_TCPA_DISCLOSURE}
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-3332" data-mp="3lwovlsast4vb">
            <button type="button" onClick={goBack} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3333" data-mp="m9oztqeonkzi">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3334" data-mp="20pedcab9j7r" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-3335" data-mp="70bsdnxyr3hm">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-3336" data-mp="pq9za0q0yqjo" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-3337" data-mp="g0i3x4a3pte5">
      {/* A. Custom Header — omitted where the site's own nav is the only
          branding the page should carry. */}
      {showBrandHeader && <header className="bg-white py-2 sm:py-4 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-3338" data-mp="18hkomdg3qv9g">
        <img src="https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp" alt="Ultimate Medical Academy Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-3339" data-mp="2t6ofhpe4up6t" />
      </header>}

      {/* B. Hero Section */}
      <section className="bg-gradient-to-r from-[#176B9C] to-[#003B5C] relative overflow-hidden" data-id="element-3340" data-mp="32wq3kh6hk7lq">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-3341" data-mp="2xyom20opkgcd" />
        {/* Explicit grid placement rather than duplicated copy: on mobile the
            form follows the headline so it lands within reach, and the
            supporting paragraph moves below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-3342" data-mp="3vk3sbs015wur">
          {/* Headline */}
          <div className="text-white max-w-xl mx-auto lg:mx-0 text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-3343" data-mp="1jmex538uxpl">
            <p className="text-[#4ECDC4] font-medium mb-3 text-lg tracking-wide" data-id="element-3344" data-mp="6hwc1l8j48du">
              Ultimate Medical Academy Online<sup className="text-xs" data-id="element-3345" data-mp="bgzfx2crw8k8">1</sup>
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-white tracking-tight" data-id="element-3346" data-mp="gglupg891wv5">
              {umaPrograms.find(p => p.slug === selectedProgram)?.shortTitle || <>
                  Healthcare
                  <br data-id="element-3347" data-mp="3jwj25hf10qps" />
                  Career Training
                </>}
            </h1>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-3348" data-mp="3toypyvd089il">
            <form onSubmit={e => e.preventDefault()} id="uma-lead-form" data-id="element-3349" data-mp="316upx5iyfyzr">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" value="" data-id="element-3350" data-mp="1ic8b2r5d3dor" />
              <input type="hidden" name="leadid_token" id="leadid_token" value="" data-id="element-3351" data-mp="1ec7qeoro6699" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#003B5C] mb-4" data-id="element-3352" data-mp="19as30q20rnnp">
                Learn how we can help you succeed.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-3353" data-mp="zeytlfjlkaa5">
                <div className="bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-3354" data-mp="umdzse0oxx6m"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-3355" data-mp="loljb6j8gjhn">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-3356" data-mp="go7avndpz7j3">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>

          {/* Supporting copy. After the form in the markup so mobile gets it
              below; the grid puts it back under the headline on desktop. */}
          <p className="text-base lg:text-lg text-white/85 max-w-lg mx-auto lg:mx-0 text-center lg:text-left leading-relaxed lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-3357" data-mp="1gtq6nkcgmj2j">
            UMA students get personalized attention throughout the entire
            education journey. Our students get support from the very beginning,
            throughout career training, and after graduation.
          </p>
        </div>
      </section>

      {/* C. Benefits Icons Row */}
      <section className="bg-[#F4F9FB] py-12 border-b border-gray-200" data-id="element-3358" data-mp="12uqisewru87f">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3359" data-mp="12x4fstb0huhm">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 text-center" data-id="element-3360" data-mp="j9zsf5yuqrf0">
            {[{
            icon: BookOpenIcon,
            title: 'One-on-one\nAcademic Support'
          }, {
            icon: AwardIcon,
            title: 'Certification Prep²',
            sup: '2'
          }, {
            icon: MonitorIcon,
            title: '24/7 Technical\nSupport'
          }, {
            icon: ClipboardCheckIcon,
            title: "Apply Degree Credits\nToward a Bachelor's³",
            sup: '3'
          }, {
            icon: BriefcaseIcon,
            title: 'Job Search\nAssistance'
          }, {
            icon: LaptopIcon,
            title: 'Laptops for those\nwho qualify⁴',
            sup: '4'
          }].map((item, idx) => <div key={idx} className="flex flex-col items-center" data-id="element-3361" data-mp="1lvti1bh874aq" data-mpi={idx}>
                <item.icon className="w-10 h-10 text-[#176B9C] mb-3 stroke-[1.5]" data-id="element-3362" data-mp="1ltyv5rme9240" />
                <p className="text-xs font-bold text-[#176B9C] whitespace-pre-line leading-tight" data-id="element-3363" data-mp="1gytd53w447l7">
                  {item.title.replace(/²|³|⁴/g, '')}
                  {item.sup && <sup className="text-[10px]" data-id="element-3364" data-mp="2ddnckdg8u1a9">{item.sup}</sup>}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* D. Three Feature Cards */}
      <section className="py-16 bg-white" data-id="element-3365" data-mp="24cfg80xafait">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3366" data-mp="24bmg3isu3j9n">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-3367" data-mp="1zd2qn8f17pcj">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/dFP2Bkreu2BvfkWxRv6Va3/Image_1.webp',
            title: 'Financial Aid',
            desc: 'Let us help you explore options for financial aid. We will help you through the process to see if you qualify.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/9FrwRXe1BYLuSp6GRYweVA/image_2.webp',
            title: 'Job Search Assistance',
            desc: 'Our Career Service team helps graduates pursue job opportunities, schedule interviews, and more.'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/qcGrjYfSLHXUMihbDJMyJz/image_3.webp',
            title: 'Engaging Online Learning',
            desc: 'Get individual instructor feedback, personalized advisor support, and interact with peers on discussion posts.'
          }].map((card, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" data-id="element-3368" data-mp="54fh36z1jbws" data-mpi={idx}>
                <img src={card.img} alt={card.title} className="w-full h-48 object-cover" data-id="element-3369" data-mp="34i9dulqz3577" />
                <div className="p-6 flex-1" data-id="element-3370" data-mp="39h2uuo5gxec4">
                  <h3 className="text-xl font-bold text-[#003B5C] mb-3" data-id="element-3371" data-mp="3dkinl9ef2ujj">
                    {card.title}
                  </h3>
                  <p className="text-sm text-[#176B9C] font-medium leading-relaxed" data-id="element-3372" data-mp="3id22c9oubvpx">
                    {card.desc}
                  </p>
                </div>
              </div>)}
          </div>
          <div className="text-center" data-id="element-3373" data-mp="2ptvckf97fosr">
            <button onClick={scrollToForm} className="inline-block bg-gradient-to-r from-[#00BFA5] to-[#4ECDC4] text-white font-bold py-3 px-12 rounded-full hover:shadow-lg transition-all" data-id="element-3374" data-mp="2psttpoly5y0h">
              Take the Next Step
            </button>
          </div>
        </div>
      </section>

      {/* E. '4 out of 5' Stat Section */}
      <section className="bg-[#F4F9FB] py-16" data-id="element-3375" data-mp="1r65z1ppodc6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-center gap-12" data-id="element-3376" data-mp="1tiy27p6o42y">
          <div className="text-center md:text-right max-w-xs" data-id="element-3377" data-mp="3584xfwvhf40o">
            <div className="text-[#00BFA5] font-bold text-5xl sm:text-6xl mb-2" data-id="element-3378" data-mp="359zh77v0oq36">
              4 <span className="text-[#176B9C]" data-id="element-3379" data-mp="3j8ph0v9813v8">out of 5</span>
            </div>
            <p className="text-[#176B9C] text-sm font-medium" data-id="element-3380" data-mp="3f62qqys77r05">
              surveyed employers say they are likely to hire a UMA graduate
              again.⁵
            </p>
          </div>
          <div className="flex gap-2 sm:gap-4" data-id="element-3381" data-mp="ugv8jji2klfw">
            {[1, 2, 3, 4].map(i => <div key={i} className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-[#D8E8F0] flex items-center justify-center bg-white" data-id="element-3382" data-mp="11zmf1x9er7o2">
                <CheckCircle2Icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#00BFA5]" data-id="element-3383" data-mp="16zh5jzp9tv8l" />
              </div>)}
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-[#D8E8F0] flex items-center justify-center bg-transparent" data-id="element-3384" data-mp="s2zt155ciakg">
              <CheckCircle2Icon className="w-10 h-10 sm:w-12 sm:h-12 text-[#D8E8F0]" data-id="element-3385" data-mp="1kq41jk3nd70z" />
            </div>
          </div>
        </div>
      </section>

      {/* F. 25% Georgetown Study Section */}
      <section className="py-16 bg-white" data-id="element-3386" data-mp="1b0at2rysx80y">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3387" data-mp="1az9j6qtiurkt">
          <div className="bg-[#003B5C] rounded-2xl overflow-hidden relative flex flex-col md:flex-row items-center shadow-xl" data-id="element-3388" data-mp="1fz48wr1k9b4a">
            <div className="p-10 md:p-16 flex-1 z-10 flex items-center gap-8" data-id="element-3389" data-mp="23nx6st1qtt1j">
              <div className="text-6xl sm:text-8xl font-bold text-white" data-id="element-3390" data-mp="28g88a0c4tmw7">
                25<span className="text-4xl sm:text-6xl" data-id="element-3391" data-mp="krdc281vulyj">%</span>
              </div>
              <div className="w-1 h-24 bg-[#00BFA5] hidden sm:block" data-id="element-3392" data-mp="kpadv0qktmiq"></div>
              <p className="text-white text-lg sm:text-xl font-medium max-w-sm leading-snug" data-id="element-3393" data-mp="fqpbovrrksrn">
                A Georgetown study found that an associate degree can increase
                lifetime earnings over a high school diploma by 25 percent.⁶
              </p>
            </div>
            <div className="w-full md:w-1/2 h-64 md:h-full absolute right-0 top-0 opacity-80 md:opacity-100" data-id="element-3394" data-mp="13gtex211iqi4">
              <img src="https://cdn.magicpatterns.com/uploads/xs9nMQM6BrsxQ4Td97ACQ9/image.png" alt="Graduate smiling" className="w-full h-full object-cover object-right" data-id="element-3395" data-mp="yk32coog2d1g" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#003B5C] to-transparent" data-id="element-3396" data-mp="otqi6xkjklvu"></div>
            </div>
          </div>
        </div>
      </section>

      {/* G. Testimonial Section */}
      <section className="bg-[#176B9C] py-16 relative" data-id="element-3397" data-mp="1hhdwxvz4siak">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3398" data-mp="1hft2s7gqb1d8">
          <div className="text-[#00BFA5] text-6xl font-serif leading-none mb-4" data-id="element-3399" data-mp="1ps35m43xdo5b">
            "
          </div>
          <p className="text-white text-xl sm:text-2xl font-medium leading-relaxed mb-8" data-id="element-3400" data-mp="1tty656e17xi1">
            "I'm a working mom and mother of four. I am thankful to UMA for the
            flexibility, so I am able to complete my courses and pass with
            honors!"
          </p>
          <div className="flex items-center justify-center gap-4" data-id="element-3401" data-mp="28omjf6poc087">
            <div className="text-right" data-id="element-3402" data-mp="2cpy18q2b3h5u">
              <div className="text-white font-bold text-lg" data-id="element-3403" data-mp="2cowqkmp7cwon">Merelesita R.</div>
              <div className="text-white/80 text-sm" data-id="element-3404" data-mp="2hir3966e4be8">UMA Graduate</div>
            </div>
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/20" data-id="element-3405" data-mp="2mfhlo05o3j2t">
              <img src="https://cdn.magicpatterns.com/uploads/xs9nMQM6BrsxQ4Td97ACQ9/image.png" alt="Merelesita R." className="w-full h-full object-cover" data-id="element-3406" data-mp="2rgos9vw2qc7w" />
            </div>
          </div>
        </div>
        {/* Triangle pointer */}
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-t-[16px] border-t-[#176B9C]" data-id="element-3407" data-mp="26qult6wep2ba"></div>
      </section>

      {/* H. Accreditation Bar */}
      <section className="bg-[#F8F9FA] py-12 mt-4" data-id="element-3408" data-mp="2bpe9pcuk8o6a">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center gap-6" data-id="element-3409" data-mp="2bnl3fynv05yt">
          <div className="w-20 h-20 flex-shrink-0 bg-white rounded-full p-2 shadow-sm flex items-center justify-center border border-gray-100" data-id="element-3410" data-mp="2pivut22wa8yf">
            <AwardIcon className="w-10 h-10 text-[#003B5C]" data-id="element-3411" data-mp="2ze78ri6hbc54" />
          </div>
          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3412" data-mp="2ufm8xjv52uh7">
            UMA is institutionally accredited by the Accrediting Bureau of
            Health Education Schools (ABHES.org), a national accrediting agency
            that is recognized by the U.S. Secretary of Education. Ultimate
            Medical Academy does not recruit or enroll residents of the European
            Union.
          </p>
        </div>
      </section>

      {/* I. Citations Section */}
      <section className="bg-[#F8F9FA] py-8 border-t border-gray-200" data-id="element-3413" data-mp="3gqdt8whndxir">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3414" data-mp="31z56qw91ks9u">
          <h4 className="text-xs font-bold text-gray-800 mb-3" data-id="element-3415" data-mp="36xzp5x33ketf">Citations</h4>
          <ol className="text-[10px] text-gray-500 space-y-1 list-decimal list-inside" data-id="element-3416" data-mp="2sz8fqnz6jhbm">
            <li data-id="element-3417" data-mp="2x0ll6wyd1ii0">
              Coursework is online except for on-site externship/professional
              practice experience. Set schedules required for on-site
              externship/professional practice experience courses in select
              programs and coursework deadlines are set by instructors.
            </li>
            <li data-id="element-3418" data-mp="h96677ubtlh9">
              Learners must meet eligibility criteria to sit for applicable
              certification exams
            </li>
            <li data-id="element-3419" data-mp="m7r3ayea7p1h">
              Tuition considerations and credit transfer decisions are at the
              discretion of the receiving institution.
            </li>
            <li data-id="element-3420" data-mp="316jdmjntg9an">
              For full information on laptop program eligibility, please see the
              UMA Catalog.
            </li>
            <li data-id="element-3421" data-mp="2wcf4btr2vp6e">
              Source: Survey was conducted by Hanover Research. Employers that
              hired UMA Online students between Jul 1, 2022 - September 30,
              2023. Survey responses received from 257 employers. Excludes
              corporate alliance partners.
            </li>
            <li data-id="element-3422" data-mp="2hjtzdtkgi29r">
              Georgetown University. The College Payoff. Study found that an
              associate degree can increase lifetime earnings over a high school
              diploma by 25 percent.
            </li>
          </ol>
          <p className="text-[10px] text-gray-500 mt-4" data-id="element-3423" data-mp="bnhmmvzme963">
            Where national data regarding earnings or employability within a
            particular field are cited, individual readers should take note that
            national averages may not accurately reflect the earnings of workers
            in their particular part of the country, and may include earners at
            all stages of their career, not just entry-level wages for recent
            graduates.
          </p>
          <p className="text-[10px] text-gray-500 mt-2" data-id="element-3424" data-mp="sy4seo4s6rk2">
            Set schedules required for on-site externship / professional
            practice experience courses in select programs and in all programs,
            coursework deadlines are set by instructors.
          </p>
        </div>
      </section>

      {/* J. UMA Footer */}
      <footer className="bg-white py-12 border-t border-gray-200" data-id="element-3425" data-mp="56zam3fa5p74">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center md:items-start gap-8" data-id="element-3426" data-mp="9axawhgcd61r">
          <img src="https://cdn.magicpatterns.com/uploads/ftg2brgTZFks7CVCNZ5jT1/UMA_LOGO_(1).webp" alt="Ultimate Medical Academy Logo" className="h-14 object-contain" data-id="element-3427" data-mp="1gmwgb3bc8nr1" />
          <div className="text-center md:text-left text-xs text-gray-600" data-id="element-3428" data-mp="1gm7sqo7n0h5w">
            <p className="mb-1" data-id="element-3429" data-mp="1lk0vf79qet1f">
              © 2026 UMA Education, Inc. d/b/a Ultimate Medical Academy. All
              rights reserved.
            </p>
            <p className="mb-4" data-id="element-3430" data-mp="1qi2gtyumr4g2">
              A member of the Vocate Education Solutions System
            </p>
            <LegalLinks className="flex justify-center md:justify-start" data-id="element-3431" data-mp="1uiuseyqz5lfi" />
          </div>
        </div>
      </footer>
    </div>;
}